import { FC, useEffect, useRef, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { VirtualizedTable } from '@/_shared/components/VirtualizedTable';
import { useDialog } from '@/_shared/hooks';
import { getAdminContentsLikesUsersCall } from '../../../../contentsLikes/state';
import { CardItem } from '../../../state';

type Props = {
  item: CardItem;
};

const TABLE_MIN_WIDTH = 600;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const UsersContentsLikesDialog: FC<Props> = ({ item }) => {
  const t = useTranslations(
    'aiPlayground.admin.landingPlayground.courses.usersContentsLikesDialog',
  );
  const tShared = useTranslations();
  const { closeDialog, Dialog, isOpen } = useDialog('usersContentsLikesDialog');
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  const {
    execute: executeGetAdminContentsLikesUsers,
    reset: resetGetAdminContentsLikesUsers,
    loading: loadingGetAdminContentsLikesUsers,
    error: errorGetAdminContentsLikesUsers,
    success: successGetAdminContentsLikesUsers,
    data,
  } = getAdminContentsLikesUsersCall.hook();

  useEffect(() => {
    if (item.contentHash !== '' && item.contentHash !== undefined) {
      executeGetAdminContentsLikesUsers({ contentHash: item.contentHash });
    }

    const widthBox = (ref.current as HTMLDivElement)?.offsetWidth;
    const PADDING = 60;
    setWidth(Math.max(widthBox - PADDING, TABLE_MIN_WIDTH));

    return () => {
      resetGetAdminContentsLikesUsers();
    };
  }, [item, executeGetAdminContentsLikesUsers, resetGetAdminContentsLikesUsers]);

  return (
    <Dialog maxWidth='1000px' open={isOpen} title={undefined} onClose={closeDialog} closeButton>
      <Box width='100%'>
        <Box mb='xs' width='100%'>
          <Box textAlign='center'>
            <Typography variant='h2'>{t('title')}</Typography>
            <Typography variant='p2/light' mt='xs'>
              {t('description')}
            </Typography>
          </Box>
          <Box mt='m' ref={ref}>
            {loadingGetAdminContentsLikesUsers && (
              <Box textAlign='center'>
                <Loader size='s' />
              </Box>
            )}
            {errorGetAdminContentsLikesUsers && (
              <AlertBadge iconName='info' type='error' label={tShared('errors.genericApiError')} />
            )}
            {successGetAdminContentsLikesUsers && (
              <Card backgroundColor='grey/dark/background' p='s' borderRadius='s'>
                <Box
                  alignItems='center'
                  display='flex'
                  flexDirection='row'
                  justifyContent='space-between'
                  mt='xxxs'
                  pb='xs'
                  mb='xs'
                >
                  <Typography variant='h3'>{t('labelAllEmployees')}</Typography>
                  <Box alignItems='center' flexDirection='row' display='flex'>
                    <Svg name='users' size='s' />
                    <Typography ml='xxs' variant='h3'>
                      {data?.length || 0}
                    </Typography>
                  </Box>
                </Box>

                {data && data.length > 0 && (
                  <VirtualizedTable
                    // eslint-disable-next-line no-nested-ternary
                    tableWidth={width}
                    data={data}
                    headerLocales='aiPlayground.admin.landingPlayground.courses.usersContentsLikesDialog.table'
                    columnWidths={{ fullName: 100 }}
                  />
                )}
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
